/**
 * WritingCoachCard
 *
 * Floating bottom-right coaching card that guides the author through the
 * 8 Writing Coach phases.
 *
 * Behaviour:
 * - Auto-shows for a blank project (no chapters with content) unless the
 *   user has previously dismissed it for this project.
 * - Detects the user's current phase from live project/chapter/character
 *   state and starts from there — never restarts from phase 1 for a
 *   returning author.
 * - Each phase has a single primary CTA that opens the right tool.
 * - Re-write phase offers two options: AI-assisted (Edit Walker) or manual.
 * - "Skip this step" advances to the next phase without opening any tool.
 * - "Don't show again for this book" dismisses per-project via localStorage.
 * - Can also be opened programmatically via the 'loom:open-coach-card' event.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Lightbulb, Map, Users, FileText, PenLine, BookOpen, RefreshCw,
  Sparkles, X, ChevronRight, ChevronLeft, Zap, Edit3,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';

// ── Phase definitions ─────────────────────────────────────────────────────────

const PHASES = [
  {
    id: 'idea',
    label: 'Idea',
    icon: Lightbulb,
    tagline: 'Every great novel starts with a clear premise.',
    steps: [
      'Choose your genre and sub-genre',
      'Set the emotional tone (dark, hopeful, tense…)',
      'Write a one-sentence logline: "X must Y before Z"',
      'Set your target word count',
    ],
    cta: 'Open Framework',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'variables' })),
    saveEvent: null,
  },
  {
    id: 'plot',
    label: 'General Plot',
    icon: Map,
    tagline: 'A structured plot prevents you from writing yourself into a corner.',
    steps: [
      'Define Act 1: opening scene, inciting incident, protagonist decision',
      'Define Act 2: rising complications, midpoint, all-is-lost moment',
      'Define Act 3: climax, resolution, final image',
      'Score your plot with the Completeness check',
    ],
    cta: 'Open Plot Builder',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-plot-builder')),
    saveEvent: 'loom:plot-saved',
  },
  {
    id: 'characters',
    label: 'Character',
    icon: Users,
    tagline: 'Readers remember characters long after they forget the plot.',
    steps: [
      'Add your protagonist — name, role, core motivation',
      'Fill in their flaw and character arc',
      'Add your antagonist (give them a real motivation, not just "evil")',
      'Add 1–2 supporting cast members',
    ],
    cta: 'Open Character Bible',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-character-bible')),
    saveEvent: null,
  },
  {
    id: 'synopsis',
    label: 'A Synopsis',
    icon: FileText,
    tagline: 'A synopsis is your GPS — you can deviate, but you\'ll never be truly lost.',
    steps: [
      'Write or generate a 2–4 paragraph synopsis covering the full arc',
      'Build a chapter outline / beat sheet (one sentence per chapter)',
      'Check that every chapter has a clear purpose',
    ],
    cta: 'Open Synopsis Builder',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-synopsis')),
    saveEvent: null,
  },
  {
    id: 'write',
    label: 'Write!',
    icon: PenLine,
    tagline: 'The first draft is just you telling the story to yourself.',
    steps: [
      'Draft Chapter 1 — establish voice, stakes, and world',
      'Use AI Assist for each chapter: draft → read → tighten',
      'Aim for your target word count before moving on',
      'Don\'t polish yet — get the whole story down first',
    ],
    cta: 'Open Editor',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'editor' })),
    saveEvent: null,
  },
  {
    id: 'read',
    label: 'Read',
    icon: BookOpen,
    tagline: 'Read your own novel like a stranger would.',
    steps: [
      'Run the Arc Walker to get your publishable score',
      'Review every flagged chapter (pacing gaps, arc breaks)',
      'Green ≥ 85 · Amber = needs work · Red = rewrite',
    ],
    cta: 'Open Arc Walker',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-arc-walker')),
    saveEvent: null,
  },
  {
    id: 'rewrite',
    label: 'Re-write',
    icon: RefreshCw,
    tagline: 'Writing is rewriting. This is where the novel becomes a novel.',
    steps: [
      'Work through any amber or red-scored chapters',
      'Use the Edit Walker for AI-assisted structural edits',
      'Or dive in and rewrite manually — both paths lead to the same place',
      'Re-run Arc Walker after major rewrites to track your score improvement',
    ],
    cta: null, // Two CTAs — handled specially in render
    ctaAi: { label: 'Open Edit Walker (AI)', action: () => window.dispatchEvent(new CustomEvent('loom:open-edit-walker')) },
    ctaManual: { label: 'I\'ll rewrite manually', action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'editor' })) },
    saveEvent: null,
  },
  {
    id: 'prose',
    label: 'Prose Edit',
    icon: Sparkles,
    tagline: 'Now that the story is right, make every sentence sing.',
    steps: [
      'Run Manuscript Polish on all chapters',
      'Accept or skip line-level edits (clarity, rhythm, word choice)',
      'Check Flesch Reading Ease — grade level should match your audience',
      'Run KDP Compliance check before publishing',
    ],
    cta: 'Open Manuscript Polish',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-flow-pass')),
    saveEvent: null,
  },
];

// ── Phase detection (mirrors WorkflowRibbon logic) ────────────────────────────

function useCurrentPhaseIndex(project, chapters, characters) {
  return useMemo(() => {
    if (!project) return 0;

    const checks = [
      // 0 — Idea
      !!(project?.genre || project?.tone || project?.logline ||
         project?.variables?.genre || project?.variables?.tone),
      // 1 — Plot
      !!(project?.plot_extraction?.extracted_at),
      // 2 — Characters
      characters.length > 0,
      // 3 — Synopsis
      !!(project?.synopsis && project.synopsis.trim().length > 20),
      // 4 — Write! (first chapter with content)
      chapters.some((c) => (c.word_count || 0) > 100 || (c.content || '').length > 100),
      // 5 — Read
      !!(project?.arc_walker_result?.publishable_score),
      // 6 — Re-write
      !!localStorage.getItem(`cr_editwalk_done_${project.id}`),
      // 7 — Prose Edit
      !!localStorage.getItem(`cr_polish_done_${project.id}`),
    ];

    const idx = checks.findIndex((done) => !done);
    return idx === -1 ? PHASES.length - 1 : idx;
  }, [project, chapters, characters]);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WritingCoachCard() {
  const { project, chapters = [], characters = [] } = usePW();

  const currentPhaseIndex = useCurrentPhaseIndex(project, chapters, characters);

  // The phase index the card is currently SHOWING (can differ from current if user skips).
  const [displayIndex, setDisplayIndex] = useState(null); // null = not yet synced
  const [visible, setVisible] = useState(false);

  const dismissKey = project?.id ? `loom:coach-dismissed-${project.id}` : null;

  // Sync displayIndex to currentPhaseIndex on initial load / project change.
  useEffect(() => {
    if (!project?.id) return;
    setDisplayIndex(currentPhaseIndex);
  }, [project?.id, currentPhaseIndex]);

  // Auto-show logic removed: Beats Coach only opens when explicitly triggered
  // from the menu (Tools → Writing Coach) or the Ribbon "?" button.
  // This prevents the card from auto-appearing on blank projects.

  // Allow opening via custom event (e.g. from ribbon "?" button).
  useEffect(() => {
    const open = () => {
      if (!project?.id) return;
      setDisplayIndex(currentPhaseIndex);
      setVisible(true);
    };
    window.addEventListener('loom:open-coach-card', open);
    return () => window.removeEventListener('loom:open-coach-card', open);
  }, [project?.id, currentPhaseIndex]);

  // ── Auto-advance on tool-save events ─────────────────────────────────────
  // When the user saves in a tool that corresponds to the CURRENT coaching
  // phase, advance the card to the next phase automatically.
  useEffect(() => {
    if (!project?.id) return;

    // Map: event name → phase id it should advance FROM
    const SAVE_EVENTS = {
      'loom:plot-saved':          'plot',
      'loom:synopsis-saved':      'synopsis',
      'loom:character-saved':     'characters',
      'loom:world-builder-saved': null, // no explicit phase — advance whichever is current
    };

    const handlers = Object.entries(SAVE_EVENTS).map(([evName, phaseId]) => {
      const handler = () => {
        setDisplayIndex((prev) => {
          const cur = prev ?? currentPhaseIndex;
          const currentPhase = PHASES[cur];
          // Only advance if the saved tool matches the card's current phase,
          // OR if phaseId is null (world-builder — always advance).
          if (phaseId === null || currentPhase?.id === phaseId) {
            const next = cur + 1;
            if (next >= PHASES.length) { setVisible(false); return cur; }
            // Do NOT auto-show — card only opens via TitleBar "Ask AI" button
            return next;
          }
          return cur;
        });
      };
      window.addEventListener(evName, handler);
      return [evName, handler];
    });

    return () => {
      handlers.forEach(([evName, handler]) => window.removeEventListener(evName, handler));
    };
  }, [project?.id, currentPhaseIndex]);

  const dismiss = useCallback(() => {
    setVisible(false);
    if (dismissKey) localStorage.setItem(dismissKey, '1');
  }, [dismissKey]);

  const skip = useCallback(() => {
    setDisplayIndex((prev) => {
      const next = (prev ?? 0) + 1;
      if (next >= PHASES.length) { setVisible(false); return prev; }
      return next;
    });
  }, []);

  const prev = useCallback(() => {
    setDisplayIndex((p) => Math.max(0, (p ?? 0) - 1));
  }, []);

  if (!visible || displayIndex === null || !project) return null;

  const phase = PHASES[displayIndex];
  const Icon = phase.icon;
  const phaseNum = displayIndex + 1;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border border-border bg-card shadow-xl ring-1 ring-black/5 dark:ring-white/5 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="writing-coach-card"
      role="complementary"
      aria-label="Writing Coach"
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between rounded-t-xl border-b border-border bg-primary/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-[11px] font-bold',
            'bg-primary',
          )}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              Writing Coach · Phase {phaseNum}/8
            </p>
            <p className="text-[13px] font-bold text-foreground leading-tight">{phase.label}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          data-testid="coach-card-hide-btn"
          className="mt-0.5 rounded-sm p-1 text-muted-foreground/60 transition-colors hover:bg-secondary hover:text-foreground"
          title="Hide for now"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* ── Progress dots ── */}
      <div className="flex items-center justify-center gap-1 px-4 pt-2.5">
        {PHASES.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setDisplayIndex(i)}
            data-testid={`coach-phase-dot-${i}`}
            title={p.label}
            className={cn(
              'h-1.5 rounded-full transition-all',
              i === displayIndex
                ? 'w-4 bg-primary'
                : i < currentPhaseIndex
                  ? 'w-1.5 bg-emerald-500'
                  : 'w-1.5 bg-border hover:bg-muted-foreground/40',
            )}
          />
        ))}
      </div>

      {/* ── Body ── */}
      <div className="px-4 py-3">
        <p className="mb-2.5 text-[12.5px] italic text-muted-foreground leading-snug">
          "{phase.tagline}"
        </p>
        <ul className="space-y-1.5 mb-3">
          {phase.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-2 text-[12px] text-foreground/85">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ul>

        {/* ── CTAs ── */}
        {phase.id === 'rewrite' ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { phase.ctaAi.action(); setVisible(false); }}
              data-testid="coach-cta-ai-rewrite"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Zap className="h-3.5 w-3.5" />
              {phase.ctaAi.label}
            </button>
            <button
              type="button"
              onClick={() => { phase.ctaManual.action(); skip(); }}
              data-testid="coach-cta-manual-rewrite"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-2 text-[12px] font-semibold text-foreground transition-colors hover:bg-secondary/80"
            >
              <Edit3 className="h-3.5 w-3.5" />
              {phase.ctaManual.label}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { phase.action(); setVisible(false); }}
            data-testid="coach-cta-primary"
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {phase.cta}
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2">
        <button
          type="button"
          onClick={prev}
          disabled={displayIndex === 0}
          data-testid="coach-card-prev-btn"
          className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        >
          <ChevronLeft className="h-3 w-3" /> Prev
        </button>
        <button
          type="button"
          onClick={skip}
          data-testid="coach-card-skip-btn"
          className="text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          Skip this step
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="coach-card-dismiss-btn"
          className="text-[11px] text-muted-foreground transition-colors hover:text-rose-600"
        >
          Don&apos;t show again
        </button>
      </div>
    </div>
  );
}
